import { render } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import { JSXInternal } from 'preact/src/jsx';
import { ConsentUrl } from 'src/Shared/UrlParser';
import { ActiveProfile } from './Config/ActiveProfile';
import { Subscribe, Unsubscribe } from '../Shared/EventPublisher/EventEmitter';
import { ConsentEventType } from '../Shared/EventPublisher/ConsentEventType';
import { RenderExplicitPanel } from './Content/Explicit';
import { RenderPreferencesPanel } from './Content/Preferences';
import { RenderVendorsPanel } from './Content/Vendors';
import { SelectionContextProvider } from './Content/shared/UiStates/SelectionsContext';
import { ConsentEventListener } from '../Shared/EventPublisher/ConsentEventListener';

const ConsentContainerId = 'GDPRConsentManagerContainer';
const parser = new DOMParser();

const generateMarkup = () => `
<div id="${ConsentContainerId}">
 
</div>
`;

const generateLink = (styleUrl: string) => `<link rel='stylesheet' type='text/css' media='all' href='${styleUrl}' />`;

const createContainer = function () {
  const styleUrl = new ConsentUrl(`${ActiveProfile.ResourcePath}/assets-TEMPLATE_ASSET_GUID/styles/core-content.css`);

  const link = parser.parseFromString(generateLink(styleUrl.toUrlString()), 'text/html').head.firstChild as HTMLElement;
  document.head.appendChild(link);

  const consentContainer = parser.parseFromString(generateMarkup(), 'text/html').body.firstChild as HTMLElement;

  consentContainer.addEventListener('click', () => 0);
  document.body.appendChild(consentContainer);

  return consentContainer;
};

type PanelComponent = () => JSXInternal.Element;

export const NullComponent: PanelComponent = () => null;

const ConsentApp = ({ Container }: { Container: HTMLElement }) => {
  const [ActivePanel, SetActivePanel] = useState<PanelComponent>(NullComponent);
  const [subscriptions, setSubscriptions] = useState<ConsentEventListener[]>([]);

  const thenSetActivePanel = (getPanel: () => Promise<PanelComponent>) => {
    return getPanel().then((p) => {
      if (!document.getElementById(ConsentContainerId)) document.body.appendChild(Container);

      SetActivePanel(() => p);
    });
  };

  function showExplicitLayer() {
    thenSetActivePanel(RenderExplicitPanel);
  }

  function showPreferencesLayer() {
    thenSetActivePanel(RenderPreferencesPanel);
  }

  function showVendorsLayer() {
    thenSetActivePanel(RenderVendorsPanel);
  }

  function hideConsentPrompt() {
    SetActivePanel(NullComponent);
    const ConsentModal = document.getElementById(ConsentContainerId);
    const parentNode = ConsentModal.parentNode as HTMLElement;
    parentNode.removeChild(ConsentModal);
  }

  const onDispose = () => {
    for (const sub of subscriptions) {
      Unsubscribe(sub);
    }

    setSubscriptions([]);
  };

  useLayoutEffect(() => {
    setSubscriptions((s) => {
      if ((s ?? []).length > 0) return s;
      return [
        Subscribe(ConsentEventType.REQUEST_SHOW_EXPLICIT_LAYER, showExplicitLayer),
        Subscribe(ConsentEventType.REQUEST_SHOW_PREFERENCES_LAYER, showPreferencesLayer),
        Subscribe(ConsentEventType.REQUEST_CLOSE_CONSENT_UI, hideConsentPrompt),
        Subscribe(ConsentEventType.REQUEST_SHOW_VENDORS_LAYER, showVendorsLayer),
      ];
    });

    return onDispose;
  }, []);

  const isNullPanel = ActivePanel === NullComponent || ActivePanel == null;

  return isNullPanel ? (
    <SelectionContextProvider></SelectionContextProvider>
  ) : (
    <SelectionContextProvider>
      <ActivePanel />
    </SelectionContextProvider>
  );
};

export const init = (): void => {
  const container = document.getElementById(ConsentContainerId) ?? createContainer();
  render(<ConsentApp Container={container} />, container);
};
